import { useNavigate, useParams, useSearchParams } from "@solidjs/router";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  Show,
} from "solid-js";
import {
  connectionTopic,
  type AdapterInfo,
  type ConnectionStatus,
  type ConnectionView,
  type DiscoveredDevice,
} from "@energyplatform/client";
import { api, live } from "../services.ts";
import { errorText, locale, t, tOr } from "../i18n.ts";
import { formatAgo, formatDateTime } from "../format.ts";
import { Icon } from "../icons.tsx";
import { CategoryBadge, categoryRank } from "../components/Media.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { AccountCredentials } from "../components/AccountCredentials.tsx";
import { groupBySource, vendorId } from "../sources.ts";
import {
  ConfirmDialog,
  ConnectionStateChip,
  Dialog,
  operator,
  PageHeader,
  RenameDialog,
  Skeleton,
  Tabs,
  useAction,
} from "../components/ui.tsx";

const MANUAL_ADAPTER = "manual";
const TABS = ["devices", "credentials", "diagnostics"] as const;
type Tab = (typeof TABS)[number];

/**
 * Integrations (prototype screen "Integracje"): vendor accounts of the organization on the left, the selected one on
 * the right with its channels grouped by device category, credentials and diagnostics. State updates live.
 */
export function ConnectionsPage() {
  const params = useParams<{ id?: string }>();
  const [search, setSearch] = useSearchParams<{ signIn?: string }>();
  const navigate = useNavigate();
  const [connections, { mutate, refetch }] = createResource(() =>
    api.connections.list(),
  );
  const [extensions] = createResource(() => api.extensions());
  const [wizard, setWizard] = createSignal(false);
  const selected = () =>
    connections()?.find((c) => c.id === params.id) ?? connections()?.[0];
  const adapterInfo = (id: string) =>
    extensions()?.adapters.find((a) => a.id === id);

  const setStatus = (status: ConnectionStatus) =>
    mutate((list) =>
      list?.map((c) => (c.id === status.connectionId ? { ...c, status } : c)),
    );

  // One subscription per listed connection; the hub only accepts this organization's topics.
  createEffect(() => {
    const ids = (connections() ?? []).map((c) => c.id);
    const off = live.on<ConnectionStatus>("connection.state", setStatus);
    const subscriptions = ids.map((id) => live.subscribe(connectionTopic(id)));
    onCleanup(() => {
      off();
      for (const s of subscriptions) {
        void s.then((unsubscribe) => unsubscribe());
      }
    });
  });

  const reload = async (select?: string) => {
    api.invalidate("/api/v1/connections");
    await refetch();
    navigate(select ? `/integrations/${select}` : "/integrations");
  };

  return (
    <>
      <PageHeader
        eyebrow={t("nav.configuration")}
        title={t("integrations.title")}
        sub={t("integrations.intro")}
      >
        <Show when={operator()}>
          <button
            type="button"
            class="btn primary"
            onClick={() => setWizard(true)}
          >
            <Icon name="plus" />
            {t("integrations.add")}
          </button>
        </Show>
      </PageHeader>
      <Show when={search.signIn}>
        {(outcome) => (
          <div class={`note ${outcome() === "ok" ? "" : "crit"}`} role="status">
            <span>
              {outcome() === "ok"
                ? t("account.signedInNote")
                : errorText(outcome())}
            </span>
            <button
              type="button"
              class="btn ghost sm"
              style={{ "margin-left": "auto" }}
              onClick={() => setSearch({ signIn: undefined })}
            >
              {t("common.close")}
            </button>
          </div>
        )}
      </Show>

      <Show
        when={!connections.error}
        fallback={<ErrorBox error={connections.error} />}
      >
        <Show
          when={connections()}
          fallback={
            <section class="card">
              <Skeleton />
            </section>
          }
        >
          <Show
            when={(connections()?.length ?? 0) > 0}
            fallback={
              <section class="card empty">
                <h2>{t("integrations.emptyTitle")}</h2>
                <p class="muted">{t("integrations.empty")}</p>
              </section>
            }
          >
            <div class="grid g-2 g-2s">
              <section class="card">
                <div class="scrollx">
                  <table class="tbl">
                    <thead>
                      <tr>
                        <th>{t("integrations.connection")}</th>
                        <th>{t("integrations.state")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={connections()}>
                        {(c) => (
                          <tr
                            class={`click ${c.id === selected()?.id ? "sel" : ""}`}
                            onClick={() => navigate(`/integrations/${c.id}`)}
                          >
                            <td>
                              <b style={{ "font-weight": 500 }}>{c.name}</b>
                              <div class="hint">
                                {tOr(`adapter.${c.adapter}`, c.adapter)}
                              </div>
                            </td>
                            <td>
                              <ConnectionStateChip state={c.status.state} />
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </section>
              <Show when={selected()} keyed>
                {(c) => (
                  <ConnectionDetail
                    connection={c}
                    adapter={adapterInfo(c.adapter)}
                    onStatus={setStatus}
                    onChanged={reload}
                  />
                )}
              </Show>
            </div>
          </Show>
        </Show>
      </Show>

      <Show when={wizard()}>
        <AddConnectionWizard
          adapters={extensions()?.adapters ?? []}
          onClose={() => setWizard(false)}
          onCreated={(id) => void reload(id)}
        />
      </Show>
    </>
  );
}

function ConnectionDetail(props: {
  connection: ConnectionView;
  adapter: AdapterInfo | undefined;
  onStatus: (s: ConnectionStatus) => void;
  onChanged: (select?: string) => Promise<void>;
}) {
  // Back from signing in at the vendor: the account settings come next (e.g. choosing installations).
  const [search] = useSearchParams<{ signIn?: string }>();
  const [tab, setTab] = createSignal<Tab>(
    props.connection.adapter === MANUAL_ADAPTER
      ? "diagnostics"
      : search.signIn === "ok"
        ? "credentials"
        : "devices",
  );
  const [run, error] = useAction();
  const [rename, setRename] = createSignal(false);
  const [remove, setRemove] = createSignal(false);
  const s = () => props.connection.status;
  const paused = () => s().state === "paused";
  const tabs = (): readonly Tab[] => (props.adapter ? TABS : ["diagnostics"]);

  return (
    <section class="card">
      <div class="card-h">
        <h2>{props.connection.name}</h2>
        <ConnectionStateChip state={s().state} />
        <span class="meta">
          {tOr(`adapter.${props.connection.adapter}`, props.connection.adapter)}
        </span>
      </div>
      <Show when={operator()}>
        <div class="panel-h">
          <button type="button" class="btn sm" onClick={() => setRename(true)}>
            <Icon name="edit" />
            {t("common.rename")}
          </button>
          <button
            type="button"
            class="btn sm"
            onClick={() =>
              void run(async () =>
                props.onStatus(
                  await (
                    paused()
                      ? api.connections.reactivate
                      : api.connections.pause
                  )(props.connection.id),
                ),
              )
            }
          >
            <Icon name={paused() ? "swap" : "pause"} />
            {paused() ? t("connections.reactivate") : t("connections.pause")}
          </button>
          <button
            type="button"
            class="btn sm danger"
            style={{ "margin-left": "auto" }}
            onClick={() => setRemove(true)}
          >
            {t("common.delete")}
          </button>
        </div>
      </Show>
      <Show when={error()}>
        <div class="card-b">
          <ErrorBox error={error()} />
        </div>
      </Show>
      <Tabs
        tabs={tabs()}
        value={tab()}
        onChange={setTab}
        label={(x) => t(`integrations.tab.${x}`)}
      />
      <Show when={tab() === "devices" && props.adapter}>
        <DiscoveredDevices connectionId={props.connection.id} />
      </Show>
      <Show when={tab() === "credentials" && props.adapter}>
        {(adapter) => (
          <Show
            when={adapter().accountSignIn}
            fallback={
              <Credentials
                connectionId={props.connection.id}
                adapter={adapter()}
              />
            }
          >
            <AccountCredentials
              connectionId={props.connection.id}
              adapter={adapter()}
            />
          </Show>
        )}
      </Show>
      <Show when={tab() === "diagnostics"}>
        <Diagnostics connection={props.connection} />
      </Show>

      <Show when={rename()}>
        <RenameDialog
          title={t("integrations.renameTitle")}
          value={props.connection.name}
          onSave={async (name) => {
            await api.connections.rename(props.connection.id, name);
            await props.onChanged(props.connection.id);
          }}
          onClose={() => setRename(false)}
        />
      </Show>
      <Show when={remove()}>
        <ConfirmDialog
          title={t("integrations.deleteTitle", { name: props.connection.name })}
          text={t("integrations.deleteText")}
          confirm={t("common.delete")}
          danger
          onConfirm={async () => {
            await api.connections.remove(props.connection.id);
            await props.onChanged();
          }}
          onClose={() => setRemove(false)}
        />
      </Show>
    </section>
  );
}

type Filter = "all" | "available" | "bound";

/** Channels the integration found, grouped by device category, with bulk binding of the suggested templates. */
function DiscoveredDevices(props: { connectionId: string }) {
  const navigate = useNavigate();
  const [discovered, { refetch }] = createResource(() =>
    api.connections.discovered(props.connectionId),
  );
  const [query, setQuery] = createSignal("");
  const [filter, setFilter] = createSignal<Filter>("all");
  const [checked, setChecked] = createSignal<ReadonlySet<string>>(new Set());
  const [collapsed, setCollapsed] = createSignal<ReadonlySet<string>>(
    new Set(),
  );
  const [expanded, setExpanded] = createSignal<ReadonlySet<string>>(new Set());
  const [run, error, busy] = useAction();

  const bindable = (d: DiscoveredDevice) =>
    d.boundDeviceId === null && d.suggestedTemplateId !== null;
  const visible = createMemo(() => {
    const q = query().trim().toLocaleLowerCase();
    return (discovered() ?? []).filter(
      (d) =>
        (filter() === "all" ||
          (filter() === "bound") === (d.boundDeviceId !== null)) &&
        (q === "" ||
          d.name.toLocaleLowerCase().includes(q) ||
          d.sourceDeviceId.toLocaleLowerCase().includes(q) ||
          d.model.toLocaleLowerCase().includes(q)),
    );
  });
  // Category → vendor device → its channels; a search opens the devices it found channels in.
  const groups = createMemo(() => groupBySource(visible(), categoryRank));
  const isOpen = (id: string) => query().trim() !== "" || expanded().has(id);
  const toggleDevice = (id: string) => {
    const next = new Set(expanded());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpanded(next);
  };
  const counts = () => {
    const all = discovered() ?? [];
    const bound = all.filter((d) => d.boundDeviceId !== null).length;
    return { all: all.length, bound, available: all.length - bound };
  };

  const toggle = (ids: string[], on: boolean) => {
    const next = new Set(checked());
    for (const id of ids) {
      if (on) {
        next.add(id);
      } else {
        next.delete(id);
      }
    }
    setChecked(next);
  };
  const toggleGroup = (category: string) => {
    const next = new Set(collapsed());
    if (next.has(category)) {
      next.delete(category);
    } else {
      next.add(category);
    }
    setCollapsed(next);
  };

  const bindChecked = () =>
    void run(async () => {
      await api.connections.bindSuggested(props.connectionId, [...checked()]);
      setChecked(new Set<string>());
      api.invalidate("/api/v1/devices");
      await refetch();
    });
  const unbind = (d: DiscoveredDevice) =>
    void run(async () => {
      await api.connections.unbind(props.connectionId, d.sourceDeviceId);
      await refetch();
    });

  return (
    <>
      <div class="panel-h">
        <label class="search">
          <Icon name="search" />
          <input
            type="search"
            placeholder={t("integrations.search")}
            value={query()}
            onInput={(e) => setQuery(e.currentTarget.value)}
          />
        </label>
        <div
          class="seg"
          role="group"
          aria-label={t("integrations.filterLabel")}
        >
          <For each={["all", "available", "bound"] as const}>
            {(f) => (
              <button
                type="button"
                aria-pressed={filter() === f}
                onClick={() => setFilter(f)}
              >
                {t(`integrations.filter.${f}`, { count: counts()[f] })}
              </button>
            )}
          </For>
        </div>
      </div>
      <Show when={operator() && checked().size > 0}>
        <div class="note">
          <span>{t("integrations.selected", { count: checked().size })}</span>
          <button
            type="button"
            class="btn primary sm"
            style={{ "margin-left": "auto" }}
            disabled={busy()}
            onClick={bindChecked}
          >
            {t("integrations.bindSelected")}
          </button>
          <button
            type="button"
            class="btn ghost sm"
            onClick={() => setChecked(new Set<string>())}
          >
            {t("common.clear")}
          </button>
        </div>
      </Show>
      <Show when={error()}>
        <div class="card-b">
          <ErrorBox error={error()} />
        </div>
      </Show>
      <Show
        when={!discovered.error}
        fallback={
          <div class="card-b">
            <ErrorBox error={discovered.error} />
          </div>
        }
      >
        <Show when={discovered()} fallback={<Skeleton />}>
          <Show
            when={visible().length > 0}
            fallback={
              <div class="card-b hint">
                {t(
                  (discovered()?.length ?? 0) === 0
                    ? "integrations.noneFound"
                    : "integrations.noMatch",
                )}
              </div>
            }
          >
            <div class="scrollx">
              <table class="tbl">
                <thead>
                  <tr>
                    <th style={{ width: "28px" }} />
                    <th>{t("integrations.col.device")}</th>
                    <th>{t("integrations.col.model")}</th>
                    <th>{t("integrations.col.platform")}</th>
                    <th class="r">{t("integrations.col.seen")}</th>
                  </tr>
                </thead>
                <For each={groups()}>
                  {(g) => {
                    const items = () => g.devices.flatMap((d) => d.channels);
                    const ids = () =>
                      items()
                        .filter(bindable)
                        .map((d) => d.sourceDeviceId);
                    const all = () =>
                      ids().length > 0 &&
                      ids().every((id) => checked().has(id));
                    return (
                      <tbody>
                        <tr class="grp">
                          <td colSpan={5}>
                            <div class="grp-in">
                              <button
                                type="button"
                                class="tw"
                                aria-expanded={!collapsed().has(g.category)}
                                onClick={() => toggleGroup(g.category)}
                              >
                                <Icon name="chev" />
                                <span class="sr-only">
                                  {t("common.toggle")}
                                </span>
                              </button>
                              <CategoryBadge category={g.category} />
                              <Show
                                when={operator() && ids().length > 0}
                                fallback={
                                  <span>{t(`category.${g.category}`)}</span>
                                }
                              >
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={all()}
                                    aria-label={t("integrations.selectGroup", {
                                      name: t(`category.${g.category}`),
                                    })}
                                    onChange={(e) =>
                                      toggle(ids(), e.currentTarget.checked)
                                    }
                                  />
                                  {t(`category.${g.category}`)}
                                </label>
                              </Show>
                              <span class="cnt">
                                {t("integrations.groupDevices", {
                                  devices: g.devices.length,
                                  bound: items().filter(
                                    (d) => d.boundDeviceId !== null,
                                  ).length,
                                  count: items().length,
                                })}
                              </span>
                            </div>
                          </td>
                        </tr>
                        <Show when={!collapsed().has(g.category)}>
                          <For each={g.devices}>
                            {(device) => {
                              const deviceIds = () =>
                                device.channels
                                  .filter(bindable)
                                  .map((d) => d.sourceDeviceId);
                              const allChannels = () =>
                                deviceIds().length > 0 &&
                                deviceIds().every((id) => checked().has(id));
                              const bound = () =>
                                device.channels.filter(
                                  (d) => d.boundDeviceId !== null,
                                ).length;
                              const seen = () =>
                                device.channels
                                  .map((d) => d.lastSeen)
                                  .sort()
                                  .at(-1)!;
                              return (
                                <>
                                  <tr class="src">
                                    <td>
                                      <Show
                                        when={
                                          operator() && deviceIds().length > 0
                                        }
                                      >
                                        <input
                                          type="checkbox"
                                          checked={allChannels()}
                                          aria-label={t(
                                            "integrations.selectDevice",
                                            { name: device.name },
                                          )}
                                          onChange={(e) =>
                                            toggle(
                                              deviceIds(),
                                              e.currentTarget.checked,
                                            )
                                          }
                                        />
                                      </Show>
                                    </td>
                                    <td>
                                      <div class="src-in">
                                        <button
                                          type="button"
                                          class="tw"
                                          aria-expanded={isOpen(device.id)}
                                          onClick={() =>
                                            toggleDevice(device.id)
                                          }
                                        >
                                          <Icon name="chev" />
                                          <span class="sr-only">
                                            {t("common.toggle")}
                                          </span>
                                        </button>
                                        <div>
                                          <div class="src-name">
                                            {device.name}
                                          </div>
                                          <div class="hint mono">
                                            {vendorId(device.id)}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td class="hint">{device.model ?? ""}</td>
                                    <td>
                                      <span
                                        class={`chip ${bound() === device.channels.length ? "ok" : bound() > 0 ? "acc" : ""}`}
                                      >
                                        {t("integrations.channelsBound", {
                                          bound: bound(),
                                          count: device.channels.length,
                                        })}
                                      </span>
                                    </td>
                                    <td
                                      class="r hint"
                                      title={formatDateTime(seen(), locale())}
                                    >
                                      {formatAgo(seen(), locale())}
                                    </td>
                                  </tr>
                                  <Show when={isOpen(device.id)}>
                                    <For each={device.channels}>
                                      {(d) => (
                                        <tr class="ch">
                                          <td>
                                            <Show
                                              when={operator() && bindable(d)}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={checked().has(
                                                  d.sourceDeviceId,
                                                )}
                                                aria-label={t(
                                                  "integrations.select",
                                                  { name: d.name },
                                                )}
                                                onChange={(e) =>
                                                  toggle(
                                                    [d.sourceDeviceId],
                                                    e.currentTarget.checked,
                                                  )
                                                }
                                              />
                                            </Show>
                                          </td>
                                          <td>
                                            <div>{d.name}</div>
                                            <div class="hint mono">
                                              {d.sourceDeviceId}
                                            </div>
                                          </td>
                                          <td class="hint">{d.model}</td>
                                          <td>
                                            <Show
                                              when={d.boundDeviceId}
                                              fallback={
                                                <Show
                                                  when={d.suggestedTemplateId}
                                                  fallback={
                                                    <span class="chip dash">
                                                      {t(
                                                        "integrations.noTemplate",
                                                      )}
                                                    </span>
                                                  }
                                                >
                                                  <span class="chip">
                                                    {t(
                                                      "integrations.available",
                                                    )}
                                                  </span>
                                                </Show>
                                              }
                                            >
                                              {(deviceId) => (
                                                <div class="row-acts">
                                                  <button
                                                    type="button"
                                                    class="btn ghost sm"
                                                    style={{
                                                      "padding-left": 0,
                                                    }}
                                                    onClick={() =>
                                                      navigate(
                                                        `/devices/${deviceId()}`,
                                                      )
                                                    }
                                                  >
                                                    <span class="chip ok">
                                                      {t("integrations.bound")}
                                                    </span>
                                                    {t("integrations.open")}
                                                  </button>
                                                  <Show when={operator()}>
                                                    <button
                                                      type="button"
                                                      class="btn ghost sm"
                                                      disabled={busy()}
                                                      onClick={() => unbind(d)}
                                                    >
                                                      {t("integrations.unbind")}
                                                    </button>
                                                  </Show>
                                                </div>
                                              )}
                                            </Show>
                                          </td>
                                          <td
                                            class="r hint"
                                            title={formatDateTime(
                                              d.lastSeen,
                                              locale(),
                                            )}
                                          >
                                            {formatAgo(d.lastSeen, locale())}
                                          </td>
                                        </tr>
                                      )}
                                    </For>
                                  </Show>
                                </>
                              );
                            }}
                          </For>
                        </Show>
                      </tbody>
                    );
                  }}
                </For>
              </table>
            </div>
          </Show>
        </Show>
      </Show>
      <div class="card-b hint">{t("integrations.identityNote")}</div>
    </>
  );
}

/** The form is generated from the fields the adapter declares; secret values are write-only and never shown again. */
function CredentialFields(props: {
  adapter: AdapterInfo;
  values: Record<string, string>;
  prefix: string;
}) {
  return (
    <For each={props.adapter.credentials.filter((f) => !f.fromAccount)}>
      {(field) => (
        <div class="field">
          <label for={`${props.prefix}-${field.name}`}>
            {tOr(`credential.${field.name}`, field.name)}
          </label>
          <Show
            when={field.options}
            fallback={
              <input
                id={`${props.prefix}-${field.name}`}
                type={field.secret ? "password" : "text"}
                required={field.required && field.default === null}
                placeholder={field.default ?? ""}
                autocomplete={field.secret ? "new-password" : "off"}
                onInput={(e) =>
                  (props.values[field.name] = e.currentTarget.value)
                }
              />
            }
          >
            {(options) => (
              <select
                id={`${props.prefix}-${field.name}`}
                value={
                  props.values[field.name] ?? field.default ?? options()[0]
                }
                onChange={(e) =>
                  (props.values[field.name] = e.currentTarget.value)
                }
              >
                <For each={options()}>
                  {(o) => (
                    <option value={o}>
                      {tOr(`credentialOption.${field.name}.${o}`, o)}
                    </option>
                  )}
                </For>
              </select>
            )}
          </Show>
          <Show when={tOr(`credentialHint.${field.name}`, "")}>
            {(hint) => <p class="hint">{hint()}</p>}
          </Show>
        </div>
      )}
    </For>
  );
}

function Credentials(props: { connectionId: string; adapter: AdapterInfo }) {
  const [state, { refetch }] = createResource(() =>
    api.connections.credentials(props.connectionId),
  );
  const [editing, setEditing] = createSignal(false);
  const [run, error, busy] = useAction();
  const values: Record<string, string> = {};

  const save = (e: SubmitEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.connections.saveCredentials(props.connectionId, { ...values });
      setEditing(false);
      await refetch();
    });
  };

  return (
    <div class="card-b stack">
      <div class="axes">
        <div class="axis">
          <span class="eyebrow">{t("integrations.credentials")}</span>
          <Show
            when={state()}
            fallback={<span class="hint">{t("common.loading")}</span>}
          >
            {(st) => (
              <Show
                when={st().configured}
                fallback={
                  <span class="chip warn">
                    {t("integration.notConfigured")}
                  </span>
                }
              >
                <span class="chip ok">{t("integration.configuredShort")}</span>
                <span class="hint">
                  {formatDateTime(st().updatedAt, locale())}
                </span>
              </Show>
            )}
          </Show>
        </div>
      </div>
      <p class="hint" style={{ margin: 0 }}>
        {t("integrations.secretNote")}
      </p>
      <Show when={operator() && !editing()}>
        <div>
          <button type="button" class="btn" onClick={() => setEditing(true)}>
            {state()?.configured
              ? t("integration.replace")
              : t("integration.configure")}
          </button>
        </div>
      </Show>
      <Show when={editing()}>
        <form class="stack" onSubmit={save} autocomplete="off">
          <CredentialFields
            adapter={props.adapter}
            values={values}
            prefix="cred"
          />
          <div class="row-acts">
            <button type="submit" class="btn primary" disabled={busy()}>
              {t("integration.save")}
            </button>
            <button type="button" class="btn" onClick={() => setEditing(false)}>
              {t("integration.cancel")}
            </button>
          </div>
        </form>
      </Show>
      <Show when={error()}>
        <ErrorBox error={error()} />
      </Show>
    </div>
  );
}

function Diagnostics(props: { connection: ConnectionView }) {
  const [discovered] = createResource(() =>
    props.connection.adapter === MANUAL_ADAPTER
      ? Promise.resolve([] as DiscoveredDevice[])
      : api.connections.discovered(props.connection.id),
  );
  const s = () => props.connection.status;
  const lastSync = () => {
    const seen = (discovered() ?? []).map((d) => d.lastSeen).sort();
    return seen[seen.length - 1] ?? null;
  };
  return (
    <div class="card-b stack">
      <div class="axes">
        <div class="axis">
          <span class="eyebrow">{t("integrations.state")}</span>
          <ConnectionStateChip state={s().state} />
        </div>
        <div class="axis">
          <span class="eyebrow">{t("integrations.failures")}</span>
          <span class="num">{s().consecutiveFailures}</span>
        </div>
        <div class="axis">
          <span class="eyebrow">{t("integrations.nextAttempt")}</span>
          <span class="num">{formatDateTime(s().nextAttemptAt, locale())}</span>
        </div>
        <div class="axis">
          <span class="eyebrow">{t("integrations.lastSync")}</span>
          <span class="num">{formatAgo(lastSync(), locale())}</span>
        </div>
      </div>
      <Show when={s().lastErrorCode}>
        {(code) => (
          <div class="err">
            <b>{tOr(`errors.${code()}`, code())}</b>
            <span class="mono">{code()}</span>
          </div>
        )}
      </Show>
      <dl class="kv">
        <dt>{t("integrations.id")}</dt>
        <dd class="mono">{props.connection.id}</dd>
        <dt>{t("integrations.adapter")}</dt>
        <dd>
          {tOr(`adapter.${props.connection.adapter}`, props.connection.adapter)}
        </dd>
        <dt>{t("integrations.created")}</dt>
        <dd>{formatDateTime(props.connection.createdAt, locale())}</dd>
        <dt>{t("integrations.channels")}</dt>
        <dd>{discovered()?.length ?? "–"}</dd>
      </dl>
    </div>
  );
}

/** Three steps: integration type, name, credentials. The connection starts once its credentials are saved. */
function AddConnectionWizard(props: {
  adapters: AdapterInfo[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [step, setStep] = createSignal(1);
  const [adapter, setAdapter] = createSignal<string>(
    props.adapters[0]?.id ?? MANUAL_ADAPTER,
  );
  const [name, setName] = createSignal("");
  const [run, error, busy] = useAction();
  const values: Record<string, string> = {};
  const info = () => props.adapters.find((a) => a.id === adapter());
  const steps = () =>
    (info()?.credentials.length ?? 0) > 0 ? [1, 2, 3] : [1, 2];
  const last = () => step() === steps().length;

  const finish = () =>
    void run(async () => {
      const created = await api.connections.create(adapter(), name().trim());
      if ((info()?.credentials.length ?? 0) > 0) {
        await api.connections.saveCredentials(created.id, { ...values });
      }
      if (info()?.accountSignIn) {
        const { url } = await api.connections.signIn(created.id);
        window.location.assign(url);
        return;
      }
      props.onClose();
      props.onCreated(created.id);
    });

  return (
    <Dialog
      title={t("integrations.wizard.title")}
      onClose={props.onClose}
      footer={
        <>
          <Show when={step() > 1}>
            <button
              type="button"
              class="btn"
              style={{ "margin-right": "auto" }}
              onClick={() => setStep(step() - 1)}
            >
              {t("common.back")}
            </button>
          </Show>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="wizardForm"
            class="btn primary"
            disabled={busy()}
          >
            {last()
              ? info()?.accountSignIn
                ? t("account.createAndSignIn", { vendor: info()!.vendor })
                : t("integrations.wizard.create")
              : t("common.next")}
          </button>
        </>
      }
    >
      <div class="wsteps">
        <For each={steps()}>
          {(n) => (
            <span class={n === step() ? "on" : n < step() ? "done" : ""}>
              <b>{n}</b>
              {t(`integrations.wizard.step${n}`)}
            </span>
          )}
        </For>
      </div>
      <form
        id="wizardForm"
        class="stack"
        autocomplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          if (last()) {
            finish();
          } else {
            setStep(step() + 1);
          }
        }}
      >
        <Show when={step() === 1}>
          <For each={[...props.adapters.map((a) => a.id), MANUAL_ADAPTER]}>
            {(id) => (
              <label class="opt">
                <input
                  type="radio"
                  name="adapter"
                  value={id}
                  checked={adapter() === id}
                  onChange={() => setAdapter(id)}
                />
                <span>
                  <b>{tOr(`adapter.${id}`, id)}</b>
                  <small>{tOr(`adapterInfo.${id}`, "")}</small>
                </span>
              </label>
            )}
          </For>
        </Show>
        <Show when={step() === 2}>
          <div class="field">
            <label for="wizName">{t("integrations.wizard.name")}</label>
            <input
              id="wizName"
              value={name()}
              maxLength={120}
              required
              autofocus
              onInput={(e) => setName(e.currentTarget.value)}
            />
            <p class="hint">{t("integrations.wizard.nameHint")}</p>
          </div>
        </Show>
        <Show when={step() === 3 && info()}>
          {(a) => (
            <>
              <p class="hint" style={{ margin: 0 }}>
                {t("integrations.secretNote")}
              </p>
              <CredentialFields adapter={a()} values={values} prefix="wiz" />
            </>
          )}
        </Show>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </form>
    </Dialog>
  );
}
